// Shared category hierarchy helpers. Categories carry a `parent_id` self-link
// but are fetched as flat rows; these build the tree / indented list / path
// used by the pickers, list filter, and product breadcrumb.

export interface Category {
  id: string;
  name: string;
  parent_id: string | null;
  status?: string | null;
}

export interface CategoryNode extends Category {
  children: CategoryNode[];
}

export function buildCategoryTree(categories: Category[]): CategoryNode[] {
  const byId = new Map<string, CategoryNode>();
  categories.forEach((c) => byId.set(c.id, { ...c, children: [] }));
  const roots: CategoryNode[] = [];
  categories.forEach((c) => {
    const node = byId.get(c.id)!;
    if (c.parent_id && byId.has(c.parent_id)) {
      byId.get(c.parent_id)!.children.push(node);
    } else {
      roots.push(node);
    }
  });
  const sort = (nodes: CategoryNode[]) => {
    nodes.sort((a, b) => a.name.localeCompare(b.name));
    nodes.forEach((n) => sort(n.children));
  };
  sort(roots);
  return roots;
}

export interface FlatCategory {
  id: string;
  name: string;
  depth: number;
}

/** Indented list for dropdowns, roots first then children. */
export function flattenWithDepth(categories: Category[]): FlatCategory[] {
  const tree = buildCategoryTree(categories);
  const out: FlatCategory[] = [];
  const walk = (nodes: CategoryNode[], depth: number) => {
    nodes.forEach((n) => {
      out.push({ id: n.id, name: n.name, depth });
      walk(n.children, depth + 1);
    });
  };
  walk(tree, 0);
  return out;
}

/** Breadcrumb path, e.g. "Clothing / Men's Clothing / T-Shirts". */
export function categoryPathName(categories: Category[], id: string | null | undefined): string {
  if (!id) return "";
  const byId = new Map(categories.map((c) => [c.id, c]));
  const parts: string[] = [];
  let cur = byId.get(id);
  let guard = 0;
  while (cur && guard < 100) {
    parts.unshift(cur.name);
    cur = cur.parent_id ? byId.get(cur.parent_id) : undefined;
    guard += 1;
  }
  return parts.join(" / ");
}

export interface DuplicateCategoryMatch {
  id: string;
  name: string;
  archived: boolean;
}

/**
 * Find an existing category with the same name (case-insensitive) under the
 * same parent. The DB already enforces uniqueness per (org, parent, lower(name)),
 * so this surfaces a friendly message instead of a raw unique-violation error.
 */
export function findDuplicateCategory(
  categories: Category[],
  name: string,
  parentId: string | null,
): DuplicateCategoryMatch | null {
  const n = name.trim().toLowerCase();
  if (!n) return null;
  const match = categories.find(
    (c) =>
      c.name.trim().toLowerCase() === n &&
      (c.parent_id ?? null) === (parentId ?? null),
  );
  if (!match) return null;
  return {
    id: match.id,
    name: match.name,
    archived: (match.status ?? "active") === "archived",
  };
}